import {Pair} from '@packages/utilities/Pair';
import {Option, None, Some} from '@packages/utilities/fp/option';

/**
 * A map from string keys to values.
 */
export type KeyMap<v> = {[key: string]: v};
export type Type<v> = KeyMap<v>;

// TODO import these from `fp/utils` once `fp/utils` no longer imports from here
const identity = <a>(x: a) => x;
const constant =
  <a>(x: a) =>
  <b>(_: b) =>
    x;
const flip =
  <a, b, c>(f: (y: a) => (x: b) => c) =>
  (x: b) =>
  (y: a) =>
    f(y)(x);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const makeEmptyObject = (): Record<string, any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const obj: Record<string, any> = {};
  if (process.env.NODE_ENV === 'development') {
    return new Proxy(obj, {
      set: (target, prop: PropertyKey, value: unknown) => {
        console.error(
          'Invalid mutation of shared constant. Property "%s" was set on "%s".',
          prop,
          'emptyObject'
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (target as any)[prop] = value;
        return true;
      },
    });
  }
  return obj;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _emptyObject: Record<string, any> = makeEmptyObject();

/**
 * Create an empty KeyMap.
 */
export const empty = <v>(): KeyMap<v> => _emptyObject as KeyMap<v>;

/**
 * Create a KeyMap with a single key-value mapping.
 */
export const singleton = <v>(key: string, val: v): KeyMap<v> =>
  ({[key]: val}) as KeyMap<v>;

/**
 * Return an array of all the keys in the KeyMap.
 * (Typed wrapper around `Object.keys`)
 */
export const keys: <v>(arg1: KeyMap<v>) => Array<string> = Object.keys;

/**
 * Return the number of entries in the map
 */
export const size = <v>(m: KeyMap<v>) => keys(m).length;

/**
 * Return an array of key-value pairs in the KeyMap.
 * (Typed wrapper around `Object.entries`)
 */
export const entries: <v>(m: KeyMap<v>) => Array<Pair<string, v>> =
  Object.entries;

/**
 * Return an array of values in the KeyMap.
 * (Typed wrapper around `Object.values`)
 */
export const values: <v>(m: KeyMap<v>) => Array<v> = Object.values;

/**
 * Check if a key is present in a KeyMap.
 */
export const has =
  (key: string) =>
  <v>(object: KeyMap<v>): boolean =>
    Object.hasOwn(object, key);

/**
 * Retrieve a value from a KeyMap by key. Returns an `Option<v>` since there
 * might not be a value at that key in the map.
 */
export const lookup = (key: string) => {
  const hasKey = has(key);
  return <v>(object: KeyMap<v>): Option<v> =>
    hasKey(object) ? Some<v>(object[key]!) : (None as Option<v>);
};

type AssocAcc<v> = {
  result: KeyMap<v>;
  source: KeyMap<v>;
};

const assocReducer = <v>(acc: AssocAcc<v>, key: string) => {
  acc.result[key] = acc.source[key]!;
  return acc;
};

/**
 * Associate a key with a value in a KeyMap.
 */
export const assoc = (key: string) => {
  const hasKey = has(key);
  return <v>(value: v) =>
    (object: KeyMap<v>): KeyMap<v> => {
      if (hasKey(object) && object[key] === value) return object;

      const result = keys(object).reduce<AssocAcc<v>>(assocReducer, {
        source: object,
        result: {} as KeyMap<v>,
      }).result;

      result[key] = value;

      return result;
    };
};

type DissocAcc<v> = {
  exclude: string;
  result: KeyMap<v>;
  source: KeyMap<v>;
};

const dissocReducer = <v>(acc: DissocAcc<v>, key: string) => {
  if (acc.exclude !== key) acc.result[key] = acc.source[key]!;
  return acc;
};

/**
 * Dissociate (remove) a key from the map.
 */
export const dissoc = (key: string) => {
  const hasKey = has(key);
  return <v>(object: KeyMap<v>) =>
    hasKey(object)
      ? keys(object).reduce<DissocAcc<v>>(dissocReducer, {
          source: object,
          result: {} as KeyMap<v>,
          exclude: key,
        }).result
      : object;
};

type OmitAcc<v> = {
  changed: boolean;
  exclude: Array<string>;
  result: KeyMap<v>;
  source: KeyMap<v>;
};

const omitReducer = <v>(acc: OmitAcc<v>, key: string) => {
  if (acc.exclude.includes(key)) {
    acc.changed = true;
  } else {
    acc.result[key] = acc.source[key]!;
  }
  return acc;
};

/**
 * Removes the specified keys from the KeyMap.
 */
export const omit = (ks: Array<string>) => {
  const len = ks.length;
  if (len === 0) return identity;
  if (len === 1) return dissoc(ks[0]!);

  return <v>(object: KeyMap<v>): KeyMap<v> => {
    const {result, changed} = keys(object).reduce<OmitAcc<v>>(omitReducer, {
      source: object,
      exclude: ks,
      changed: false,
      result: {} as KeyMap<v>,
    });

    return changed ? result : object;
  };
};

const pickReducer = <v>(acc: AssocAcc<v>, key: string) => {
  if (Object.hasOwn(acc.source, key)) {
    acc.result[key] = acc.source[key]!;
  }
  return acc;
};

/**
 * Keeps only the specified keys in the KeyMap.
 */
export const pick =
  (ks: Array<string>) =>
  <v>(source: KeyMap<v>) =>
    ks.reduce<AssocAcc<v>>(pickReducer, {source, result: {} as KeyMap<v>})
      .result;

type PickByAcc<v> = {
  changed: boolean;
  predicate: (x: v) => boolean;
  result: KeyMap<v>;
  source: KeyMap<v>;
};

const pickByReducer = <v>(acc: PickByAcc<v>, key: string) => {
  const value = acc.source[key]!;
  if (acc.predicate(value)) {
    acc.result[key] = value;
  } else {
    acc.changed = true;
  }
  return acc;
};

/**
 * Only keep values in the KeyMap for which the predicate holds.
 */
export const pickBy =
  <v>(predicate: (x: v) => boolean) =>
  (object: KeyMap<v>) => {
    const {result, changed} = keys(object).reduce<PickByAcc<v>>(pickByReducer, {
      source: object,
      predicate,
      changed: false,
      result: {} as KeyMap<v>,
    });
    return changed ? result : object;
  };

/**
 * Modify the value at the specified key using the provided function.
 */
export const adjust =
  <v>(f: (x: v) => v) =>
  (key: string) => {
    const hasKey = has(key);
    return (obj: KeyMap<v>) =>
      hasKey(obj) ? assoc(key)(f(obj[key]!))(obj) : obj;
  };

/**
 * Transform each value in the KeyMap using the provided function.
 */
export const map =
  <a, b>(f: (val: a) => b) =>
  (obj: KeyMap<a>): KeyMap<b> => {
    let changed = false;
    const newObj = keys(obj).reduce<KeyMap<b>>((result, key) => {
      const oldVal = obj[key]!;
      const newVal = f(oldVal);
      if ((oldVal as unknown) !== newVal) {
        changed = true;
      }
      result[key] = newVal;
      return result;
    }, {} as KeyMap<b>);
    return changed ? newObj : (obj as unknown as KeyMap<b>);
  };

/**
 * Compare two KeyMap maps for equality. The provided function is used to
 * compare values.
 */
export const equal =
  <v>(eqVal: (v1: v, v2: v) => boolean) =>
  (o1: KeyMap<v>, o2: KeyMap<v>) => {
    if (o1 === o2) return true;
    const k1 = keys(o1);
    const k2 = keys(o2);
    if (k1.length !== k2.length) return false;
    for (const k of k1)
      if (!Object.hasOwn(o2, k) || !eqVal(o1[k]!, o2[k]!)) return false;
    return true;
  };

/**
 * Merge two KeyMap maps. The provided function is used to determine how to
 * combine values for keys which are present in both maps.
 */
export const unionWith =
  <v>(combine: (x: v) => (y: v) => v) =>
  (first: KeyMap<v>) =>
    first === empty()
      ? identity
      : (second: KeyMap<v>) => {
          if (second === empty()) return first;

          let changedFromFirst = false;
          let changedFromSecond = false;
          const result = {} as KeyMap<v>;

          for (const key in second) {
            const secondVal = second[key]!;

            if (key in first) {
              const firstVal = first[key]!;
              const finalVal = combine(firstVal)(secondVal);

              if (finalVal !== secondVal) {
                changedFromSecond = true;
              }
              if (finalVal !== firstVal) {
                changedFromFirst = true;
              }
              result[key] = finalVal;
            } else {
              changedFromFirst = true;
              result[key] = secondVal;
            }
          }

          for (const key in first) {
            if (key in result) continue;
            changedFromSecond = true;
            result[key] = first[key]!;
          }

          if (!changedFromFirst) return first;
          if (!changedFromSecond) return second;
          return result;
        };

/**
 * Merge two KeyMap maps. Values from the first win.
 */
export const union: <v>(x: KeyMap<v>) => (y: KeyMap<v>) => KeyMap<v> =
  unionWith(constant);

/**
 * Merge two KeyMap maps. Values from the second win.
 */
export const unionTo: <v>(arg1: KeyMap<v>) => (arg2: KeyMap<v>) => KeyMap<v> =
  flip(union);
